"use client"
import { Box, Stack, VStack } from "@chakra-ui/react"
import parse from "html-react-parser"
import { type FC } from "react"

import BlurFade from "@/components/Animations/BlurFade"
import ClientZoom from "@/components/ClientZoom"
import MarkdownParser from "@/components/MarkdownParser"
import { type SVGBlock as SVGBlockType } from "@/types/database"
import { getSafeHtmlParserOptions } from "@/utils/getSafeHtmlParserOptions"
import getStickyContentBlockLeftProps from "@/utils/getStickyContentBlockLeftProps"

import ContentBlockTitle, {
  type ContentBlockTitleNumberProps,
} from "./ContentBlockTitle"

interface SVGBlockProps extends ContentBlockTitleNumberProps {
  block: SVGBlockType
}

const SVGBlock: FC<SVGBlockProps> = ({ block, titleNumber }) => {
  if (!block) return null // guard when block is undefined
  const svg = block.svg?.trim()
  if (!svg) return null

  const blockWithLegacyFields = block as SVGBlockType & {
    darker_bg_svg?: boolean
  }
  const darker_bg =
    block.darker_bg || blockWithLegacyFields.darker_bg_svg || false
  const two_columns = block.two_columns_svg || false
  const title = block.title
  const trimmedTitle = title?.trim()
  const zoom = block.zoom ?? true
  const description = (block as any).description // in case description is added in future
  const hasTitle =
    trimmedTitle !== undefined && trimmedTitle !== "" && trimmedTitle !== null
  const hasDescription =
    description !== undefined && description !== "" && description !== null
  const parsedSvg = parse(svg, getSafeHtmlParserOptions())

  return (
    <Stack
      alignItems="flex-start"
      bgColor={darker_bg ? "bg.subtle" : "transparent"}
      className="svg-block internal-padding"
      direction={{
        base: "column",
        lg: two_columns ? "row" : "column",
      }}
      gap={{ base: 8, lg: two_columns ? 6 : 8 }}
      justifyContent="flex-end"
    >
      {(hasTitle || hasDescription) && (
        <VStack
          alignItems="flex-start"
          flexGrow={1}
          width={{
            base: "100%",
            lg: two_columns ? "30%" : "100%",
          }}
          {...getStickyContentBlockLeftProps(two_columns)}
        >
          <BlurFade flexDirection="column" gap={0} inView>
            <ContentBlockTitle title={trimmedTitle} titleNumber={titleNumber} />
            {hasDescription && (
              <MarkdownParser
                color="fg.muted"
                fontSize="sm"
                markdown={description}
                pProps={{ fontFamily: "mono" }}
              />
            )}
          </BlurFade>
        </VStack>
      )}
      <VStack
        alignItems="flex-start"
        flexGrow={1}
        maxWidth={{
          base: "100%",
          lg: two_columns ? "70%" : "100%",
        }}
        minW={0}
        pl={!hasTitle && two_columns ? { base: 0, lg: 8 } : 0}
        w={{
          base: "100%",
          lg: two_columns ? "70%" : "100%",
        }}
      >
        <Box
          css={{
            "& [data-rmiz-content], & [data-rmiz]": {
              width: "100%",
            },
            "& svg": {
              display: "block",
              height: "auto",
              maxWidth: "100%",
              width: "100%",
            },
          }}
          w="full"
        >
          <BlurFade inView>
            {zoom ? (
              <ClientZoom isDisabled={!zoom}>{parsedSvg}</ClientZoom>
            ) : (
              parsedSvg
            )}
          </BlurFade>
        </Box>
      </VStack>
    </Stack>
  )
}

export default SVGBlock
